import type Performer from "parog-web/models/performer";
import type Prescriptor from "parog-web/models/prescriptor";
import {
  performerRetrieve,
  prescriptorRetrieve
} from "parog-web/utils/dmp-utils";
import { arrayIfNeeded } from "parog-web/utils/xml-parsed-helpers";
import type { Execution, Section } from "./abstract-dmp-parser";
import AbstractDmpParser from "./abstract-dmp-parser";

interface Prestation {
  sourceConcept: string;
  sourceValue: string;
  sourceLabel: string;
  quantity: number;
}

interface VaccinesExecution extends Execution {
  prestations: Array<Prestation>;
  prescriptor: Prescriptor;
  performer: Performer;
}

export default class VaccinesDmpParser extends AbstractDmpParser {
  journalEventClassName = "VaccinesRefundedJournalEvent";

  canParse(section: Section): boolean {
    const code = section.code?._attributes?.code;
    const title = section.title?._text;
    return code == "11369-6" && (title == "Vaccinations" || title == "Vaccins");
  }

  parse(section: Section): Array<VaccinesExecution> {
    return this.getEntries(section)
      .map(entry => {
        let vaccineObject;

        const vaccine = entry.substanceAdministration;

        if (vaccine) {
          vaccineObject = this.parseVaccine(vaccine);
        }

        if (vaccineObject) {
          return vaccineObject;
        }
      })
      .filter(Boolean) as VaccinesExecution[];
  }

  private parseVaccine(vaccine): VaccinesExecution | undefined {
    const manufacturedMaterial =
      vaccine.consumable?.manufacturedProduct?.manufacturedMaterial;

    if (
      manufacturedMaterial.code?._attributes?.nullFlavor ||
      manufacturedMaterial.code?._attributes?.codeSystem == "1.2.250.1.213.2.63"
    )
      return;

    let sourceConcept;
    let sourceValue;
    let sourceLabel;
    let quantity;

    const cisSource = manufacturedMaterial.code?._attributes;
    const translations = arrayIfNeeded(manufacturedMaterial.code?.translation);
    const cipSource = translations.find(
      translation =>
        translation._attributes?.codeSystem == "1.2.250.1.215.200.1.1.1"
    );
    const ucdSource = translations.find(
      translation =>
        translation._attributes?.codeSystem == "1.2.250.1.215.200.1.1.2"
    );

    if (cipSource) {
      sourceConcept = "CIP";
      sourceValue = cipSource._attributes?.code;
      sourceLabel = cipSource._attributes?.displayName;
    } else if (cisSource) {
      sourceConcept = cisSource.codeSystem;
      sourceValue = cisSource.code;
      sourceLabel = cisSource.displayName;
    } else if (ucdSource) {
      sourceConcept = "UCD";
      sourceValue = ucdSource._attributes?.code;
      sourceLabel = ucdSource._attributes?.displayName;
    }

    if (!sourceConcept || !sourceValue || !sourceLabel) return;

    let prescriptionEntryRelationship;
    if (Array.isArray(vaccine.entryRelationship)) {
      prescriptionEntryRelationship = vaccine.entryRelationship?.find(
        relationship => relationship._attributes?.typeCode == "REFR"
      );
    } else {
      if (vaccine.entryRelationship?._attributes?.typeCode == "REFR") {
        prescriptionEntryRelationship = vaccine.entryRelationship;
      }
    }

    let occurredAt;
    if (prescriptionEntryRelationship) {
      quantity = parseInt(
        prescriptionEntryRelationship.supply?.quantity?._attributes?.value
      );
      occurredAt = this.parseOccurredAt(
        prescriptionEntryRelationship,
        "supply.performer.time._attributes.value"
      );
    }

    if (!occurredAt) return;

    return {
      occurredAt,
      prestations: [{ sourceConcept, sourceValue, sourceLabel, quantity }],
      prescriptor: prescriptorRetrieve(
        vaccine.entryRelationship?.supply?.author
      ),
      performer: performerRetrieve(vaccine.entryRelationship?.supply?.performer)
    };
  }
}
